import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { formatMoney, parseDecimal } from "@/lib/formatters"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures } from "@/features/staff/reports/report-block"
import { formatHourLabel, tStats, weekdayLabel } from "@/features/staff/stats/format"
import type { Granularity, WeekdayHour } from "@/features/staff/stats/types"

/**
 * Sales by hour and weekday: the staffing map, and the one figure on this screen
 * that gets MORE trustworthy the longer the window is.
 *
 * **Magnitude, so one hue in five ordinal steps** — the `--heat-*` ramp, which
 * is Chile Ancho put to the one job it can do without breaking the Chile rule:
 * "hotter is busier" is the metaphor a restaurateur reads with no legend at all.
 *
 * **A cell with no sales is `--muted`, never step 1.** An hour the restaurant
 * was shut and an hour it was slow are different facts, and a ramp that merged
 * them would invite a manager to staff a shift that does not exist.
 *
 * Only weekdays and hours the restaurant actually sold in are drawn. A grid
 * padded out to 24 columns is mostly empty and says nothing.
 */
const STEPS = ["bg-heat-1", "bg-heat-2", "bg-heat-3", "bg-heat-4", "bg-heat-5"] as const

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]

export function StatsHourMap({
  cells,
  granularity,
  closedBucketCount,
}: {
  cells: WeekdayHour[]
  granularity: Granularity
  closedBucketCount: number
}) {
  if (cells.length === 0) return null

  // How much SERVICE the map was built from, which is not the same as how many
  // points the chart above it has. Thirty days is four weeks — the range that
  // most needs this warning is the one a bucket count would never flag, because
  // twenty-nine closed days reads as plenty until you notice it is a month.
  const thin =
    granularity === "day" ? true : granularity === "week" ? closedBucketCount < 8 : false

  const hours = Array.from(new Set(cells.map((cell) => cell.hour))).sort((a, b) => a - b)
  const byKey = new Map(cells.map((cell) => [`${cell.weekday}-${cell.hour}`, cell]))
  const max = cells.reduce((top, cell) => Math.max(top, parseDecimal(cell.subtotal)), 0)
  const peak = cells.reduce<WeekdayHour | null>(
    (top, cell) => (top === null || parseDecimal(cell.subtotal) > parseDecimal(top.subtotal) ? cell : top),
    null
  )

  const step = (cell: WeekdayHour | undefined) => {
    if (!cell || max <= 0) return "bg-muted"
    const value = parseDecimal(cell.subtotal)
    if (value <= 0) return "bg-muted"
    const index = Math.min(STEPS.length - 1, Math.floor((value / max) * STEPS.length))
    return STEPS[index]
  }

  return (
    <Card density="compact" data-testid="stats-hour-map">
      <CardHeader className="border-b">
        <CardTitle>{tStats("hour_map.title")}</CardTitle>
        <CardDescription>{tStats("notes.hour_map_basis")}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Scrolls inside its own container: the page body never scrolls
            sideways, which is the gutter rule. */}
        <div className="overflow-x-auto">
          <div className="min-w-max space-y-[3px]">
            {/* The spacer mirrors the day-label cell exactly, gap included. A
                `pl-11` header would sit 3px left of the column it names, because
                the label contributes its own trailing gap. */}
            <div className="flex gap-[3px]">
              <span className="w-11 shrink-0" aria-hidden="true" />
              {hours.map((hour) => (
                <span
                  key={hour}
                  className="w-11 shrink-0 text-center text-xs tabular-nums text-muted-foreground"
                >
                  {String(hour).padStart(2, "0")}
                </span>
              ))}
            </div>
            {WEEKDAYS.map((weekday) => (
              <div key={weekday} className="flex items-center gap-[3px]">
                <span className="w-11 shrink-0 text-xs text-muted-foreground">
                  {weekdayLabel(weekday)}
                </span>
                {hours.map((hour) => {
                  const cell = byKey.get(`${weekday}-${hour}`)
                  return (
                    <span
                      key={hour}
                      data-testid={`stats-hour-cell-${weekday}-${hour}`}
                      title={cell ? `${formatMoney(cell.subtotal)}` : tStats("hour_map.no_sales")}
                      className={`h-7 w-11 shrink-0 rounded ${step(cell)}`}
                    />
                  )
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">{tStats("hour_map.less")}</span>
            <span className="flex gap-0.5">
              {STEPS.map((tone) => (
                <span key={tone} className={`h-2.5 w-5 rounded-sm ${tone}`} />
              ))}
            </span>
            <span className="text-xs text-muted-foreground">{tStats("hour_map.more")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-5 rounded-sm bg-muted" />
            <span className="text-xs text-muted-foreground">{tStats("hour_map.no_sales")}</span>
          </div>
        </div>

        <ReportFigures>
          <TillFigure
            label={tStats("hour_map.peak")}
            value={
              peak
                ? `${weekdayLabel(peak.weekday)} ${formatHourLabel(peak.hour)}`
                : tStats("hour_map.peak_none")
            }
            hint={peak ? formatMoney(peak.subtotal) : undefined}
            testId="stats-hour-map-peak"
          />
        </ReportFigures>

        {thin ? (
          <p className="text-xs text-muted-foreground" data-testid="stats-hour-map-thin">
            {tStats("notes.hour_map_thin")}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
